import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { residentialUnitReceiptAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import './ResidentialUnitReceiptCreate.css';

const ResidentialUnitReceiptView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadResidentialUnitReceipt();
  }, [id]);

  const loadResidentialUnitReceipt = async () => {
    try {
      setLoading(true);
      const response = await residentialUnitReceiptAPI.getById(id);
      const data = response.data;
      setReceipt({
        ...data,
        startDate: data.startDate ? new Date(data.startDate).toISOString().split('T')[0] : '',
        endDate: data.endDate ? new Date(data.endDate).toISOString().split('T')[0] : '',
        isActive: data.isActive ? 'true' : 'false',
      });
    } catch (err) {
      setErrorMessage(t('residentialUnitReceipt.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">{t('residentialUnitReceipt.list.messages.noData')}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header-actions">
        <button className="btn-back" onClick={() => navigate('/residential-unit-receipt')}>
          <i className="fas fa-arrow-left"></i> {t('residentialUnitReceipt.view.backToList')}
        </button>
        <button className="btn-edit" onClick={() => navigate(`/residential-unit-receipt/edit/${id}`)}>
          <i className="fas fa-edit"></i> {t('residentialUnitReceipt.view.edit')}
        </button>
      </div>

      <div className="form-container">
        {/* Unit Information */}
        <h3 className="form-section-title">{t('residentialUnitReceipt.view.sections.unitInformation')}</h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-file-contract"></i> {t('residentialUnitReceipt.view.fields.contractNumber')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-file-contract input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.contractNumber}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-calendar"></i> {t('residentialUnitReceipt.view.fields.startDate')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-calendar input-icon"></i>
              <input
                type="date"
                className="form-input"
                value={receipt.startDate}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-calendar"></i> {t('residentialUnitReceipt.view.fields.endDate')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-calendar input-icon"></i>
              <input
                type="date"
                className="form-input"
                value={receipt.endDate}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-building"></i> {t('residentialUnitReceipt.view.fields.propertyName')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-building input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.propertyName}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-home"></i> {t('residentialUnitReceipt.view.fields.unitNumber')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-home input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.unitNumber}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-check-circle"></i> {t('residentialUnitReceipt.view.fields.isActive')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-check-circle input-icon"></i>
              <select
                className="form-input"
                value={receipt.isActive}
                disabled
              >
                <option value="true">{t('common.yes')}</option>
                <option value="false">{t('common.no')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* First Party (Lessor) */}
        <h3 className="form-section-title">{t('residentialUnitReceipt.view.sections.firstParty')}</h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-user"></i> {t('residentialUnitReceipt.view.fields.firstPartyName')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-user input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.firstPartyName}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-id-card"></i> {t('residentialUnitReceipt.view.fields.firstPartyIdNumber')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-id-card input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.firstPartyIdNumber}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-id-badge"></i> {t('residentialUnitReceipt.view.fields.firstPartyIdType')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-id-badge input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.firstPartyIdType || '-'}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-user-tag"></i> {t('residentialUnitReceipt.view.fields.firstPartyStatus')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-user-tag input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.firstPartyStatus || '-'}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-check-circle"></i> {t('residentialUnitReceipt.view.fields.firstPartyReceiptStatus')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-check-circle input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.firstPartyReceiptStatus || '-'}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-sticky-note"></i> {t('residentialUnitReceipt.view.fields.firstPartyNotes')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-sticky-note input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  value={receipt.firstPartyNotes || ''}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        {/* Second Party (Lessee) */}
        <h3 className="form-section-title">{t('residentialUnitReceipt.view.sections.secondParty')}</h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-user"></i> {t('residentialUnitReceipt.view.fields.secondPartyName')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-user input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.secondPartyName}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-id-card"></i> {t('residentialUnitReceipt.view.fields.secondPartyIdNumber')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-id-card input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.secondPartyIdNumber}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-id-badge"></i> {t('residentialUnitReceipt.view.fields.secondPartyIdType')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-id-badge input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.secondPartyIdType || '-'}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-user-tag"></i> {t('residentialUnitReceipt.view.fields.secondPartyStatus')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-user-tag input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.secondPartyStatus || '-'}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-check-circle"></i> {t('residentialUnitReceipt.view.fields.secondPartyReceiptStatus')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-check-circle input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={receipt.secondPartyReceiptStatus || '-'}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default ResidentialUnitReceiptView;
